import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EnvironmentService } from '@core/services/infra/environment.service';

@Component({
    selector: 'vex-environment-indicator',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconModule,
        MatTooltipModule
    ],
    template: `
    @if (shouldShowIndicator()) {
      <div
        class="environment-indicator"
        [class.development]="envService.isDevelopment"
        [class.staging]="envService.isStaging"
        [class.production]="envService.isProduction"
        [class.unknown]="!envService.isDevelopment && !envService.isStaging && !envService.isProduction"
        [matTooltip]="getTooltip()">
        <mat-icon>{{ getIcon() }}</mat-icon>
        <span class="environment-name">{{ displayName() }}</span>
        @if (envService.showDebugInfo) {
          <span class="project-id">{{ envService.firebaseProjectId }}</span>
        }
      </div>
    }
    `,
    styles: [`
    .environment-indicator {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 28px;
      padding: 0 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      border: 1px solid transparent;
      transition: background-color 0.15s ease, border-color 0.15s ease;
      max-width: 100%;
    }

    .development {
      background: rgb(var(--ds-success-soft));
      color: rgb(var(--ds-success));
      border-color: rgb(var(--ds-success) / 0.2);
    }

    .staging {
      background: rgb(var(--ds-warning-soft));
      color: rgb(var(--ds-warning));
      border-color: rgb(var(--ds-warning) / 0.2);
    }

    .production {
      background: rgb(var(--ds-danger-soft));
      color: rgb(var(--ds-danger));
      border-color: rgb(var(--ds-danger) / 0.2);
    }

    .unknown {
      background: rgb(var(--ds-hairline));
      color: rgb(var(--ds-text-2));
      border-color: rgb(var(--ds-card-border));
    }

    .environment-name {
      font-weight: 600;
      text-transform: none;
      /* Sentence case — DESIGN.md §2; was screaming uppercase. */
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .project-id {
      font-size: 10px;
      font-weight: 500;
      opacity: 0.85;
      background: rgb(var(--ds-surface) / 0.55);
      padding: 1px 5px;
      border-radius: 6px;
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    /* Icon-only below desktop so the right cluster stays calm. */
    @media (max-width: 959px) {
      .environment-indicator {
        width: 32px;
        height: 32px;
        padding: 0;
        gap: 0;
      }

      .environment-name,
      .project-id {
        display: none;
      }
    }
  `]
})
export class EnvironmentIndicatorComponent {

  constructor(public envService: EnvironmentService) {}

  /** Sentence-case label — env configs store SCREAMING_SNAKE names. */
  displayName(): string {
    return this.envService.environmentName
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  shouldShowIndicator(): boolean {
    // Always show in development and staging
    // Only show in production if debug mode is enabled
    return !this.envService.isProduction || this.envService.showDebugInfo;
  }

  getIcon(): string {
    if (this.envService.isDevelopment) return 'code';
    if (this.envService.isStaging) return 'science';
    if (this.envService.isProduction) return 'warning';
    return 'help';
  }

  getTooltip(): string {
    const envInfo = this.envService.getEnvironmentInfo();
    return `Environment: ${envInfo.name}
Project: ${envInfo.projectId}
Features: Debug=${envInfo.features.debugMode}, Mock=${envInfo.features.mockData}`;
  }
}
